import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Blast, HoleService, Hole } from "src/app/services/hole.service";
import { BlastDesignInformationsComponent } from "./blast-design-informations/blast-design-informations.component";
import { BlastDesignDataComponent } from "./blast-design-data/blast-design-data.component";
import { HolesListDataComponent } from "./holes-list-data/holes-list-data.component";
import * as THREE from "three";
import { FonctionsService } from "src/app/services/fonctions-partagees.service";
import { UtilisateurService } from "src/app/services/utilisateur.service";
import { SiteHoleTolerance } from "src/app/bo/site-hole-tolerance";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "app-blast-design",
  templateUrl: "./blast-design.component.html",
  styleUrls: ["./blast-design.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BlastDesignDataComponent,
    HolesListDataComponent,
    CommonModule,
    TranslateModule
  ],
})
export class BlastDesignComponent {
  readonly tirId = input.required<number>();
  readonly blastInput = input<Blast | null>(null, { alias: 'blast' });

  protected readonly blast = signal<Blast | null>(null);
  protected readonly holeSelected = signal<string | null>(null);
  protected readonly isInformationsCollapsed = signal(false);
  protected readonly qualityData = signal<SiteHoleTolerance | null>(null);

  protected readonly dataComponentClass = computed(() =>
    `col-md-${12 - (this.isInformationsCollapsed() ? 1 : 5)}`
  );

  private loadSeq = 0;

  constructor(
    private readonly holeService: HoleService,
    private readonly fonctionsService: FonctionsService,
    private readonly userService: UtilisateurService
  ) {
    effect(() => {
      const id = this.tirId();
      if (id) this.getBlastHoles(id);
    });

    this.getHoleQuality();

    effect(() => {
      const b = this.blastInput();
      if (b && !this.blast()) {
        this.blast.set(this.addCalculatedValuesToBlast(b));
      }
    });
  }
  
  
  private getHoleQuality() {
    const siteId = this.userService.currentSite()?.siteId;
    if (!siteId) return;
    this.holeService.getSiteHoleTolerance(siteId).subscribe({
      next: (tolerance) => {
        this.qualityData.set(tolerance);
      },
      error: (error) => {
        console.error("Error while fetching site hole tolerance", error);
      },
    });
  }

  private getBlastHoles(id: number, policy: "cache-first" | "network-only" = "cache-first"): void {
    const seq = ++this.loadSeq;
    this.fonctionsService.loading_screen(true, 20000);
    this.holeService.GetBlastDesign(id, policy).then((b: Blast) => {
      if (seq !== this.loadSeq) return;
      this.blast.set(this.addCalculatedValuesToBlast(b));
      this.fonctionsService.loading_screen(false, 20000);
    });
  }

  onAddHole(): void {
    this.getBlastHoles(this.tirId(), "network-only");
  }

  onHoleSelected(event: { holeId: string | null; changeCollapsed: boolean }): void {
    this.holeSelected.set(event.holeId);
    if (event.holeId && event.changeCollapsed) {
      this.isInformationsCollapsed.set(false);
    }
  }

  onCollapseChanged(isCollapsed: boolean): void {
    this.isInformationsCollapsed.set(isCollapsed);
  }

  private addCalculatedValuesToBlast(blast: Blast): Blast {
    if (!blast?.BlastDesign) {
      return blast;
    }

    return {
      ...blast,
      BlastDesign: blast.BlastDesign.map((hole) => {
        const completedHole = { ...hole };
        completedHole.calculatedValues = {
          headPositionMagnitude: this.calculateHeadPositionMagnitude(hole),
          toePositionMagnitude: this.calculateToePositionMagnitude(hole),
          toeLevelData: this.calculateToeLevelData(hole),
        };
        return completedHole;
      }),
    };
  }

  private calculateHeadPositionMagnitude(hole: Hole): number | null {
    if (!hole.PositionMeasuredXYZ || !hole.PositionPlannedXYZ) {
      return null;
    }

    const deltaX =
      (hole.PositionMeasuredXYZ.x - hole.PositionPlannedXYZ.x) * 100;
    const deltaY =
      (hole.PositionMeasuredXYZ.y - hole.PositionPlannedXYZ.y) * 100;

    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  }

  private calculateCompleteToePosition(
    headPosition: { x: number; y: number; z: number },
    length: number,
    angle: number,
    azimuth: number
  ): { x: number; y: number; z: number } {
    const dipRad = angle;
    const azimuthRad = azimuth;
    const horizontalLength = length * Math.sin(dipRad);

    return {
      x: headPosition.x + horizontalLength * Math.sin(azimuthRad),
      y: headPosition.y + horizontalLength * Math.cos(azimuthRad),
      z: headPosition.z - length * Math.sin(dipRad)
    };
  }

  private calculateToePositionMagnitude(hole: Hole): number | null {
    if (
      !hole.PositionPlannedXYZ ||
      !hole.Geometry.Design ||
      !hole.Geometry.Measure
    ) {
      return null;
    }

    try {
      const plannedToe = this.calculateCompleteToePosition(
        hole.PositionPlannedXYZ,
        hole.Geometry.Design.length,
        hole.Geometry.Design.angle,
        hole.Geometry.Design.azimuth
      );
      const measuredToe = this.calculateCompleteToePosition(
        hole.PositionMeasuredXYZ ?? hole.PositionPlannedXYZ,
        hole.Geometry.Measure.length,
        hole.Geometry.Measure.angle,
        hole.Geometry.Measure.azimuth
      );

      const deltaX = (measuredToe.x - plannedToe.x) * 100;
      const deltaY = (measuredToe.y - plannedToe.y) * 100;
      const deltaZ = (measuredToe.z - plannedToe.z) * 100;

      return Math.sqrt(deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ);
    } catch (error) {
      console.error("Erreur lors du calcul de la magnitude Toe:", error);
      return null;
    }
  }

  private calculateToeLevelData(hole: Hole): {
    planned: number | null;
    measured: number | null;
    difference: number | null;
  } {
    try {
      let plannedToeZ = null;
      if (hole.PositionPlannedXYZ && hole.Geometry.Design) {
        plannedToeZ = this.calculateCompleteToePosition(
          hole.PositionPlannedXYZ,
          hole.Geometry.Design.length,
          hole.Geometry.Design.angle,
          hole.Geometry.Design.azimuth
        ).z;
      }

      let measuredToeZ = null;
      if (hole.Geometry.Measure) {
        measuredToeZ = this.calculateCompleteToePosition(
          hole.PositionMeasuredXYZ ?? hole.PositionPlannedXYZ,
          hole.Geometry.Measure.length,
          hole.Geometry.Measure.angle,
          hole.Geometry.Measure.azimuth
        ).z;
      }

      if (plannedToeZ == null && measuredToeZ == null) {
        return { planned: null, measured: null, difference: null };
      }

      const plannedToeLevel = plannedToeZ;
      const measuredToeLevel = measuredToeZ;
      const difference = (measuredToeLevel == null || plannedToeLevel == null) ? 0 : measuredToeLevel - plannedToeLevel;

      return {
        planned: plannedToeLevel,
        measured: measuredToeLevel,
        difference: difference,
      };
    } catch (error) {
      console.error("Erreur lors du calcul du niveau Toe:", error);
      return { planned: null, measured: null, difference: null };
    }
  }
}